/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { decorate, computed, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Radio } from 'semantic-ui-react';
import c from 'classnames';
import Header from '@amzn/base-ui/dist/parts/helpers/fields/Header';
import Description from '@amzn/base-ui/dist/parts/helpers/fields/Description';
import ErrorPointer from '@amzn/base-ui/dist/parts/helpers/fields/ErrorPointer';
import { nicePrice } from '@amzn/base-ui/dist/helpers/utils';

// expected props
// - configurations (via props) and array of the compute configurations MST
// - formField (via props) an instance of the mobx form field
class SelectConfigurationCards extends React.Component {
  get configurations() {
    return this.props.configurations;
  }

  get configurationId() {
    return this.formField.value;
  }

  get formField() {
    return this.props.formField;
  }

  handleSelectConfigurationId = configId => {
    this.formField.sync(configId);
    this.formField.resetValidation();
  };

  render() {
    const field = this.formField;
    const { error = '' } = field;
    const hasError = !_.isEmpty(error);
    const isDisabled = field.disabled;
    const disabledClass = isDisabled ? 'disabled' : '';
    const errorClass = hasError ? 'error' : '';

    return (
      <div className={c('mb4', errorClass, disabledClass)}>
        <Header field={field} />
        <Description field={field} />
        <ErrorPointer field={field} className="mb1" />
        {this.renderCards()}
      </div>
    );
  }

  renderCards() {
    const disabled = this.formField.disabled;
    const configurations = this.configurations;
    const isSelected = config => config.id === this.configurationId;

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px', marginTop: '8px' }}>
        {_.map(configurations, config => (
          <div
            key={config.id}
            className={c('compute-card', { selected: isSelected(config) })}
            onClick={() => !disabled && this.handleSelectConfigurationId(config.id)}
          >
            <div className="compute-card-header">
              <Radio checked={isSelected(config)} disabled={disabled} />
              <span className="compute-card-title">{config.title}</span>
            </div>
            <div className="compute-card-body">
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: config.descHtml }} />
            </div>
            {this.renderCardInfo(config)}
          </div>
        ))}
      </div>
    );
  }

  renderCardInfo(config) {
    const priceTitle = item => {
      const isSpot = _.get(item, 'priceInfo.type') === 'spot';
      return isSpot ? 'Maximum price per day' : 'Price per day';
    };
    const region = item => _.get(item, 'priceInfo.region');
    const price = item => {
      const perDay = item.pricePerDay;
      if (_.isUndefined(perDay) || (_.isString(perDay) && _.isEmpty(perDay))) return 'N/A';
      return `$${nicePrice(perDay)}`;
    };

    return (
      <div className="compute-card-info">
        {_.map(config.displayProps).map((property, propertyIndex) => (
          <div className="compute-card-info-row" key={propertyIndex}>
            <span className="compute-card-info-key">{property.key}</span>
            <span className="compute-card-info-value">{property.value}</span>
          </div>
        ))}
        <div className="compute-card-info-row">
          <span className="compute-card-info-key">
            {priceTitle(config)}
            {region(config) && <span style={{ fontSize: '0.85em', color: '#9ca3af' }}> ({region(config)})</span>}
          </span>
          <span className="compute-card-info-value">{price(config)}</span>
        </div>
      </div>
    );
  }
}

decorate(SelectConfigurationCards, {
  configurations: computed,
  configurationId: computed,
  formField: computed,
  handleSelectConfigurationId: action,
});

export default inject()(observer(SelectConfigurationCards));
